import { useState, useEffect, useRef, useCallback } from "react";
import { supabase, fuzz, milesBetween, bearingBetween } from "./supabaseClient";

// Central backend hook: auth session, profile, and live nearby users.
export function useOscarBackend() {
  const [session, setSession] = useState(null);
  const [authReady, setAuthReady] = useState(false);
  const [profile, setProfile] = useState(null);
  const [authError, setAuthError] = useState("");
  const [busy, setBusy] = useState(false);
  const [liveUsers, setLiveUsers] = useState([]); // {id,name,age,bio,verified,dist,bearing}
  const [incoming, setIncoming] = useState([]); // pending requests TO me
  const [sentPings, setSentPings] = useState([]);   // pings I sent
  const [recvPings, setRecvPings] = useState([]);   // pings I received
  const [connections, setConnections] = useState([]); // accepted (either direction)
  const [messages, setMessages] = useState([]); // all my messages
  const [members, setMembers] = useState([]); // all registered members (for browse gallery)
  const myPos = useRef(null);

  // ---- fetch all registered members (browse gallery, not just live) ----
  const loadMembers = useCallback(async () => {
    try {
      // Progressive fallback: ask for everything, then drop optional columns
      // (photos, then wallet) so a not-yet-added column can never empty the list.
      let res = await supabase.from("profiles").select("id,name,age,bio,gender,seeking,city,state,country,verified,photo,photos,premium").limit(80);
      if (res.error) {
        res = await supabase.from("profiles").select("id,name,age,bio,gender,seeking,city,state,country,verified,photo,photos").limit(80);
      }
      if (res.error) {
        res = await supabase.from("profiles").select("id,name,age,bio,gender,seeking,city,state,country,verified,photo").limit(80);
      }
      if (res.error) return;
      const mine = session && session.user ? session.user.id : null;
      setMembers((res.data || []).filter(p => p && p.name && p.id !== mine));
    } catch (e) {}
  }, [session]);
  useEffect(() => {
    loadMembers();
    const iv = setInterval(loadMembers, 30000);
    return () => clearInterval(iv);
  }, [loadMembers]);

  // ---- session bootstrap ----
  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => { setSession(data.session); setAuthReady(true); }).catch(() => setAuthReady(true));
    const { data: sub } = supabase.auth.onAuthStateChange((_e, s) => setSession(s));
    return () => sub.subscription.unsubscribe();
  }, []);

  // ---- load my profile when logged in ----
  useEffect(() => {
    if (!session) { setProfile(null); return; }
    supabase.from("profiles").select("*").eq("id", session.user.id).single()
      .then(({ data }) => setProfile(data));
  }, [session]);

  const signUp = useCallback(async (email, password, fields) => {
    try { localStorage.setItem("oscar_last_active", String(Date.now())); } catch (e) { /* ignore */ }
    setBusy(true); setAuthError("");
    const redirectTo = (typeof window !== "undefined" && window.location ? window.location.origin : undefined);
    const { data, error } = await supabase.auth.signUp({ email, password, options: redirectTo ? { emailRedirectTo: redirectTo } : undefined });
    if (error) {
      let msg = error.message || "Signup failed";
      if ((error.status && error.status >= 500) || /confirmation|sending|smtp|mail/i.test(msg)) {
        msg = "Couldn't send the confirmation email. In Supabase, set up email (SMTP) or temporarily turn off 'Confirm email'.";
      }
      setAuthError(msg); setBusy(false); return { ok: false, error: msg };
    }
    const uid = data.user?.id;
    if (uid) {
      try {
        let up = await supabase.from("profiles").upsert({
          id: uid, name: fields.name, age: parseInt(fields.age) || null, age_min: parseInt(fields.ageMin)||null, age_max: parseInt(fields.ageMax)||null,
          bio: fields.bio || "", gender: fields.gender || "", seeking: fields.seeking || "", city: fields.city || "", state: fields.state || "", country: fields.country || "",
          verified: !!fields.verified, photo: fields.photo || null
        });
        if (up && up.error && /city|state|country|column|schema/i.test(up.error.message || "")) {
          // location columns may not exist yet — create the row without them
          await supabase.from("profiles").upsert({
            id: uid, name: fields.name, age: parseInt(fields.age) || null,
            bio: fields.bio || "", gender: fields.gender || "", seeking: fields.seeking || "",
            verified: !!fields.verified, photo: fields.photo || null
          });
        }
      } catch (e) {}
    }
    setBusy(false);
    return { ok: true, needsVerify: !data.session };
  }, []);

  const signIn = useCallback(async (email, password) => {
    setBusy(true); setAuthError("");
    // A fresh sign-in is activity: clear any stale idle stamp so the idle check can not instantly sign the user out again.
    try { localStorage.setItem("oscar_last_active", String(Date.now())); } catch (e) { /* ignore */ }
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) { setAuthError(error.message); setBusy(false); return false; }
    setBusy(false); return true;
  }, []);

  // ---- update my profile row (name, bio, photo, etc.) ----
  const updateProfile = useCallback(async (fields) => {
    if (!session) return { ok: false, error: "Not signed in" };
    const row = { id: session.user.id };
    if (fields.name !== undefined) row.name = fields.name;
    if (fields.age !== undefined) row.age = parseInt(fields.age) || null;
    if (fields.bio !== undefined) row.bio = fields.bio || "";
    if (fields.gender !== undefined) row.gender = fields.gender || "";
    if (fields.seeking !== undefined) row.seeking = fields.seeking || "";
    if (fields.city !== undefined) row.city = fields.city || "";
    if (fields.state !== undefined) row.state = fields.state || "";
    if (fields.country !== undefined) row.country = fields.country || "";
    if (fields.phone !== undefined) row.phone = fields.phone || "";
    if (fields.sub_canceled !== undefined) row.sub_canceled = !!fields.sub_canceled;
    // NOTE: verified / phone_verified are intentionally NOT writable here.
    // They are set server-side only (verify-face / check-verification functions,
    // via the service role) so a client can never grant itself a trust badge.
    if (fields.photo !== undefined) row.photo = fields.photo || null;
    if (fields.photos !== undefined) row.photos = fields.photos || [];
    if (fields.compat_score !== undefined) row.compat_score = fields.compat_score;
    if (fields.compat_prof !== undefined) row.compat_prof = fields.compat_prof;
    const { id, ...changes } = row;
    // UPDATE (not upsert): the row already exists from sign-up. Upsert would attempt an
    // INSERT first, which fails NOT NULL on columns like "name" when we only send photos.
    let { error } = await supabase.from("profiles").update(changes).eq("id", id);
    if (error && /photos|compat|phone_verified|phone|sub_canceled|city|state|country|column|schema/i.test(error.message || "")) {
      // optional columns may not exist yet — retry with only the core columns
      const { photos, compat_score, compat_prof, phone_verified, phone, sub_canceled, city, state, country, ...rest } = changes;
      const retry = await supabase.from("profiles").update(rest).eq("id", id);
      error = retry.error;
    }
    if (error) { console.log("[oscar] updateProfile error:", error.message); return { ok: false, error: error.message }; }
    setProfile(p => Object.assign({}, p, row));
    return { ok: true };
  }, [session]);

  // ---- update auth email / password ----
  const updateAccount = useCallback(async ({ email, password }) => {
    if (!session) return { ok: false, error: "Not signed in" };
    const payload = {};
    if (email) payload.email = email;
    if (password) payload.password = password;
    if (!Object.keys(payload).length) return { ok: true };
    const { error } = await supabase.auth.updateUser(payload);
    if (error) { console.log("[oscar] updateAccount error:", error.message); return { ok: false, error: error.message }; }
    return { ok: true };
  }, [session]);

  const goOffline = useCallback(async () => {
    try { if (session) await supabase.from("locations").delete().eq("user_id", session.user.id); } catch (e) {}
  }, [session]);
  const signOut = useCallback(async () => {
    if (session) await supabase.from("locations").delete().eq("user_id", session.user.id);
    await supabase.auth.signOut();
    setProfile(null); setLiveUsers([]);
  }, [session]);

  // ---- broadcast my (fuzzed) location periodically ----
  const lastPush = useRef({ lat: 999, lon: 999, t: 0 });
  const pushLocation = useCallback(async (lat, lon) => {
    if (!session) { return; }
    myPos.current = { lat, lon };
    // De-dupe: skip if we haven't moved ~40m and pushed less than 12s ago.
    // (watchPosition + the 15s heartbeat were both firing, spamming writes.)
    const now = Date.now(), lp = lastPush.current;
    const same = Math.abs(lat - lp.lat) < 0.0004 && Math.abs(lon - lp.lon) < 0.0004;
    if (same && now - lp.t < 12000) return;
    lastPush.current = { lat, lon, t: now };
    const f = fuzz(lat, lon);
    const { error } = await supabase.from("locations").upsert({
      user_id: session.user.id, lat: f.lat, lon: f.lon, updated_at: new Date().toISOString()
    }, { onConflict: "user_id" });
    if (error) console.log("[oscar] pushLocation error:", error.message);

  }, [session]);

  // ---- fetch nearby live users (active in last 2 min) ----
  const refreshNearby = useCallback(async (rangeMi) => {
    if (!session || !myPos.current) return;
    const since = new Date(Date.now() - 150000).toISOString();
    const { data: locs } = await supabase.from("locations")
      .select("user_id,lat,lon,updated_at").gte("updated_at", since);
    if (!locs) return;
    const others = locs.filter(l => l.user_id !== session.user.id);
    const ids = others.map(o => o.user_id);
    let profs = {};
    if (ids.length) {
      const { data: ps } = await supabase.from("profiles").select("*").in("id", ids);
      (ps || []).forEach(p => { profs[p.id] = p; });
    }
    const me = myPos.current;
    const list = others.map(o => {
      const d = milesBetween(me.lat, me.lon, o.lat, o.lon);
      const b = bearingBetween(me.lat, me.lon, o.lat, o.lon);
      const p = profs[o.user_id] || {};
      return {
        id: o.user_id, name: p.name || "Member", age: p.age, bio: p.bio,
        verified: p.verified, dist: Math.max(0.1, d), bearing: b,
        photo: p.photo || (p.photos && p.photos[0]) || null,
        photos: (p.photos && p.photos.length) ? p.photos : (p.photo ? [p.photo] : []),
        gender: p.gender || "", seeking: p.seeking || "any",
        city: p.city || "", state: p.state || "", country: p.country || ""
      };
    }).filter(u => u.dist <= (rangeMi || 5));
    setLiveUsers(list);
  }, [session]);

  // ---- send a ping to a real user ----
  const sendPing = useCallback(async (toUserId) => {
    if (!session) return;
    await supabase.from("pings").upsert({ from_user: session.user.id, to_user: toUserId }, { onConflict: "from_user,to_user" });
    loadPings();
  }, [session]);

  // ---- connections ----
  const requestConnect = useCallback(async (toUserId) => {
    if (!session) return;
    await supabase.from("connections").upsert(
      { from_user: session.user.id, to_user: toUserId, status: "pending" },
      { onConflict: "from_user,to_user" });
  }, [session]);

  const respondConnect = useCallback(async (fromUserId, accept) => {
    if (!session) return;
    await supabase.from("connections")
      .update({ status: accept ? "accepted" : "rejected" })
      .eq("from_user", fromUserId).eq("to_user", session.user.id);
    // if accepted, create reverse row so both see it as a connection
    if (accept) {
      await supabase.from("connections").upsert(
        { from_user: session.user.id, to_user: fromUserId, status: "accepted" },
        { onConflict: "from_user,to_user" });
    }
  }, [session]);

  const loadConnections = useCallback(async () => {
    if (!session) return;
    const uid = session.user.id;
    const { data, error } = await supabase.from("connections").select("*")
      .or("from_user.eq." + uid + ",to_user.eq." + uid);
    if (error) { console.log("[oscar] loadConnections error:", error.message); return; }
    if (!data) return;
    const inc = data.filter(c => c.to_user === uid && c.status === "pending");
    // resolve requester names
    if (inc.length) {
      const ids = inc.map(c => c.from_user);
      const { data: ps } = await supabase.from("profiles").select("id,name").in("id", ids);
      const nm = {}; (ps || []).forEach(p => nm[p.id] = p.name);
      inc.forEach(c => { c.fromName = nm[c.from_user] || "Someone"; });
    }
    setIncoming(inc);
    setConnections(data.filter(c => c.status === "accepted"));
  }, [session]);

  // ---- messages ----
  const sendMessage = useCallback(async (toUserId, body) => {
    if (!session || !body.trim()) return;
    await supabase.from("messages").insert({ from_user: session.user.id, to_user: toUserId, body: body.trim() });
  }, [session]);

  const loadMessages = useCallback(async () => {
    if (!session) return;
    const uid = session.user.id;
    const { data } = await supabase.from("messages").select("*")
      .or("from_user.eq." + uid + ",to_user.eq." + uid)
      .order("created_at", { ascending: true });
    if (data) setMessages(data);
  }, [session]);

  const loadPings = useCallback(async () => {
    if (!session) return;
    const uid = session.user.id;
    const { data, error } = await supabase.from("pings").select("*")
      .or("from_user.eq." + uid + ",to_user.eq." + uid)
      .order("created_at", { ascending: false });
    if (error || !data) return;
    const ids = Array.from(new Set(data.map(p => (p.from_user === uid ? p.to_user : p.from_user))));
    const prof = {};
    if (ids.length) {
      const { data: ps } = await supabase.from("profiles").select("id,name,photo,photos,age,verified").in("id", ids);
      (ps || []).forEach(p => { prof[p.id] = p; });
    }
    const sSeen = {}, rSeen = {}, sent = [], recv = [];
    data.forEach(p => {
      const other = p.from_user === uid ? p.to_user : p.from_user;
      const pr = prof[other] || {};
      const e = { id: other, realId: other, name: pr.name || "Member", age: pr.age || "", verified: !!pr.verified, photo: pr.photo || (pr.photos && pr.photos[0]) || null, at: p.created_at };
      if (p.from_user === uid) { if (!sSeen[other]) { sSeen[other] = 1; sent.push(e); } }
      else { if (!rSeen[other]) { rSeen[other] = 1; recv.push(e); } }
    });
    setSentPings(sent); setRecvPings(recv);
  }, [session]);

  const deletePing = useCallback(async (otherId, dir) => {
    if (!session || !otherId) return;
    const uid = session.user.id;
    let q = supabase.from("pings").delete();
    if (dir === "sent") q = q.eq("from_user", uid).eq("to_user", otherId);
    else if (dir === "recv") q = q.eq("from_user", otherId).eq("to_user", uid);
    else q = q.or("and(from_user.eq." + uid + ",to_user.eq." + otherId + "),and(from_user.eq." + otherId + ",to_user.eq." + uid + ")");
    try { await q; } catch (e) {}
    loadPings();
  }, [session, loadPings]);

  // realtime: refresh connections + messages on any change
  useEffect(() => {
    if (!session) return;
    loadConnections(); loadMessages(); loadPings();
    const ch = supabase.channel("oscar-rt")
      .on("postgres_changes", { event: "*", schema: "public", table: "connections" }, loadConnections)
      .on("postgres_changes", { event: "*", schema: "public", table: "messages" }, loadMessages)
      .on("postgres_changes", { event: "*", schema: "public", table: "pings" }, loadPings)
      .subscribe();
    const poll = setInterval(() => { loadConnections(); loadMessages(); loadPings(); }, 5000);
    return () => { supabase.removeChannel(ch); clearInterval(poll); };
  }, [session, loadConnections, loadMessages, loadPings]);

  const isConnected = useCallback((otherId) => {
    return connections.some(c =>
      (c.from_user === otherId || c.to_user === otherId));
  }, [connections]);

  // Upload a photo (data URL or blob) to Supabase Storage and return a public URL.
  const uploadPhoto = useCallback(async (dataUrl) => {
    if (!session) return { ok: false, error: "Not signed in" };
    try {
      const resp = await fetch(dataUrl);
      const blob = await resp.blob();
      const ext = ((blob.type && blob.type.split("/")[1]) || "jpg").replace("jpeg", "jpg");
      const path = session.user.id + "/" + Date.now() + "_" + Math.random().toString(36).slice(2, 8) + "." + ext;
      const { error } = await supabase.storage.from("photos").upload(path, blob, { contentType: blob.type || "image/jpeg", upsert: true });
      if (error) return { ok: false, error: error.message };
      const { data } = supabase.storage.from("photos").getPublicUrl(path);
      return { ok: true, url: (data && data.publicUrl) || null, path };
    } catch (e) {
      return { ok: false, error: String(e && e.message ? e.message : e) };
    }
  }, [session]);

  // --- Moderation: reports sink ---------------------------------------------
  // Persists a report to the `reports` table. If the table doesn't exist yet
  // (or the insert fails for any reason), the report is queued in localStorage
  // so it is never lost, and flushed on next login.
  const reportUser = useCallback(async (reportedId, reportedName, reason, details) => {
    const reporter = session && session.user ? session.user.id : null;
    const row = {
      reporter_id: reporter,
      reported_id: reportedId || null,
      reported_name: reportedName || "",
      reason: reason || "",
      details: details || ""
    };
    try {
      const { error } = await supabase.from("reports").insert(row);
      if (!error) return { ok: true, local: false };
      console.log("[oscar] reportUser cloud insert failed, queuing locally:", error.message);
    } catch (e) {
      console.log("[oscar] reportUser threw, queuing locally:", e && e.message);
    }
    try {
      const raw = localStorage.getItem("oscar_reports_pending");
      const arr = raw ? JSON.parse(raw) : [];
      arr.push(Object.assign({ at: Date.now() }, row));
      localStorage.setItem("oscar_reports_pending", JSON.stringify(arr.slice(-50)));
      return { ok: false, local: true };
    } catch (e) {
      return { ok: false, local: false };
    }
  }, [session]);

  const flushPendingReports = useCallback(async () => {
    let arr;
    try {
      const raw = localStorage.getItem("oscar_reports_pending");
      arr = raw ? JSON.parse(raw) : [];
    } catch (e) { return; }
    if (!arr || !arr.length) return;
    const remaining = [];
    for (const r of arr) {
      try {
        const { at, ...row } = r;
        const { error } = await supabase.from("reports").insert(row);
        if (error) remaining.push(r);
      } catch (e) { remaining.push(r); }
    }
    try {
      if (remaining.length) localStorage.setItem("oscar_reports_pending", JSON.stringify(remaining));
      else localStorage.removeItem("oscar_reports_pending");
    } catch (e) {}
  }, []);

  // ---- profile views (who looked at me) ----
  const [views, setViews] = useState([]);
  const loadViews = useCallback(async () => {
    if (!session) { setViews([]); return; }
    try {
      const { data, error } = await supabase.from("profile_views").select("viewer_id,created_at").eq("viewed_id", session.user.id).order("created_at", { ascending: false }).limit(60);
      if (!error && data) setViews(data);
    } catch (e) {}
  }, [session]);
  useEffect(() => {
    loadViews();
    if (!session) return undefined;
    const iv = setInterval(loadViews, 60000);
    return () => clearInterval(iv);
  }, [loadViews, session]);
  const recordView = useCallback(async (viewedId) => {
    if (!session || !viewedId || viewedId === session.user.id) return;
    try { await supabase.from("profile_views").insert({ viewer_id: session.user.id, viewed_id: viewedId }); } catch (e) {}
  }, [session]);

  return {
    session, authReady, profile, authError, busy, liveUsers, members,
    incoming, connections, messages, sentPings, recvPings,
    requestConnect, respondConnect, sendMessage, isConnected,
    signUp, signIn, signOut, goOffline, pushLocation, refreshNearby, sendPing, deletePing, updateProfile, updateAccount, uploadPhoto,
    reportUser, flushPendingReports, recordView, views,
    loggedIn: !!session
  };
}
